import api from './apiClient';

// --- CONFIG TYPES ---
export interface BillingConfig {
  [key: string]: any;
  CATALOG?: {
    version: string;
    currency: string;
    packageDiscount: number;
    products: Array<{
      code: string;
      name: string;
      category: string;
      description: string;
      monthlyUsd: number;
      setupUsd: number;
      monthly: number;
      setup: number;
      quantityMode: string;
      storageBytes?: number;
      package: boolean;
      entitlements: Record<string, number | boolean>;
      requiresOutput: boolean;
      supportedCountries?: string[];
    }>;
  };
  /* Legacy plan configuration remains available while non-billing controls migrate. */
  BROADCASTER?: {
    label: string;
    currency: string;
    setupFee: number;
    monthlyCost: number;
    limits?: { platform: number; channel: number; transcode?: number };
    pricing?: any;
    addons?: any;
    upgrades?: any;
    defaults?: any;
    type: 'TIERED' | 'ELASTIC' | 'OUTPUT_BASED';
  };
}

export interface InitiatePaymentPayload {
  paymentGateway: 'manual';
  action?: 'ACTIVATE' | 'RENEW' | 'UPGRADE' | 'ADD_RESOURCE' | 'BATCH_ADD_RESOURCE' | 'BILLING_ANCHOR_CHANGE';
  targetPlan?: string;
  options?: any;
}

export interface PaymentResponse {
  success: boolean;
  message: string;
  redirectUrl?: string;
  intent?: any;
  reviewStatus?: 'PENDING_ADMIN_REVIEW';
  purchasedItems?: string;
}

export interface NextInvoice {
  amount: number;
  currency: string;
  lines?: BillingIntentLine[];
  breakdown?: {
    recurring?: number;
    setupFee?: number;
    lines?: BillingIntentLine[];
    prorationMultiplier?: number;
    effectiveBillingDate?: string | null;
    billingPeriodStart?: string | null;
    billingPeriodEnd?: string | null;
    remainingDays?: number | null;
    fullPeriodDays?: number | null;
    [key: string]: any;
  };
  metadata?: any;
  description?: string;
}

export interface BillingIntentLine {
  code?: string;
  description: string;
  quantity: number;
  unitPrice: number;
  totalAmount: number;
  currency?: string;
  isProrated?: boolean;
  metadata?: Record<string, any>;
}

export interface BillingServiceLine {
  type: string;
  name: string;
  description?: string;
  quantity: number;
  unitPrice: number;
  monthlyAmount: number;
  status: string;
  metadata?: Record<string, any>;
}

export interface BillingChannelSummary {
  id: number;
  name: string;
  isActive: boolean;
  billingStatus?: string;
  currentPeriodStart?: string | null;
  dueDate?: string | null;
  graceEndsAt?: string | null;
  gracePeriodDays?: number;
  limits: {
    outputs: number;
    direct?: number;
    socialStream: number;
    managed: number;
    hls: number;
    hlsPlus: number;
  };
  usage: {
    activeOutputs: number;
    configuredOutputs: number;
    directConfigured: number;
    socialConfigured: number;
    managedConfigured: number;
    hlsConfigured: number;
    hlsPlusConfigured: number;
  };
  services: BillingServiceLine[];
  monthlyAmount: number;
}

export interface BillingInvoiceItem {
  id: number;
  lineType: string;
  code?: string | null;
  description: string;
  quantity: number;
  unitPrice: number;
  totalAmount: number;
  isProrated: boolean;
}

export interface BillingInvoiceSummary {
  id: number;
  channelId?: string | null;
  channelName?: string | null;
  invoiceNumber: string;
  title: string;
  status: string;
  kind: string;
  currency: string;
  subtotal: number;
  taxAmount: number;
  discountAmount: number;
  totalAmount: number;
  amountPaid: number;
  balanceDue: number;
  issueDate?: string | null;
  dueDate?: string | null;
  paidAt?: string | null;
  latestPaymentStatus?: string | null;
  canRetry?: boolean;
  canCancelAttempt?: boolean;
  items: BillingInvoiceItem[];
}

export interface BillingPaymentSummary {
  id: number;
  invoiceId?: number | null;
  amount: number;
  currency: string;
  gateway: string;
  status: string;
  description?: string | null;
  transactionId?: string | null;
  providerReference?: string | null;
  initiatedAt?: string | null;
  paidAt?: string | null;
  failedAt?: string | null;
  refundedAt?: string | null;
  items?: Array<{
    id: number;
    description: string;
    quantity: number;
    totalAmount: number;
  }>;
}

export interface ResourceCancellation {
  id: number;
  userId: number;
  channelId: number;
  resourceType: string;
  resourceId?: string | null;
  replacementResourceId?: string | null;
  quantity: number;
  status: string;
  effectiveAt?: string | null;
  reason?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface BillingSubscriptionSummary {
  id: number;
  planCode: string;
  planLabel: string;
  status: string;
  billingInterval: string;
  currency: string;
  startDate?: string | null;
  currentPeriodStart?: string | null;
  currentPeriodEnd?: string | null;
  nextBillingDate?: string | null;
  cancelAtPeriodEnd: boolean;
}

export interface BillingAccountSummary {
  account: {
    id: number;
    email: string;
    phone?: string | null;
    role: string;
    status: string;
    currency: string;
    billingCycle: string;
    effectiveBillingDate?: string | null;
    billingCycleEndsAt?: string | null;
    trialExpiresAt?: string | null;
    setupFeePaid: boolean;
    gracePeriodDays: number;
    gracePeriod: {
      canRequest: boolean;
      requestWindowOpensAt?: string | null;
      approvedUntil?: string | null;
      maxHours: number;
      latestRequest?: {
        id: number;
        status: string;
        requestedAt?: string | null;
        approvedHours?: number | null;
        approvedUntil?: string | null;
        reviewedAt?: string | null;
        note?: string | null;
      } | null;
    };
    pendingBillingAnchorAdjustment?: {
      id: number;
      amount: number;
      currency: string;
      oldBillingCycleEndsAt?: string | null;
      requestedBillingCycleEndsAt?: string | null;
      extensionDays: number;
      dueOn?: string | null;
    } | null;
    billingContact: {
      firstName?: string | null;
      lastName?: string | null;
      country?: string | null;
      city?: string | null;
      postalCode?: string | null;
    };
  };
  plan: {
    code: string;
    label: string;
    type: string;
    currency: string;
    setupFee: number;
    monthlyCost: number;
    pricing?: Record<string, any>;
    addons?: Record<string, any>;
    defaults?: Record<string, any>;
    limits?: Record<string, any>;
  } | null;
  nextInvoice?: NextInvoice | null;
  usage: {
    channels: number;
    limits: {
      channels: number;
      outputs: number;
      socialStream: number;
      managed: number;
      hls: number;
      hlsPlus: number;
    };
    used: {
      outputs: number;
      activeOutputs: number;
      direct: number;
      socialStream: number;
      managed: number;
      hls: number;
      hlsPlus: number;
    };
  };
  services: {
    channels: BillingChannelSummary[];
    monthlyAmount: number;
  };
  invoices: BillingInvoiceSummary[];
  payments: BillingPaymentSummary[];
  pendingPurchaseReviews: Array<{
    id: number;
    gateway: string;
    amount: number;
    currency: string;
    submittedAt?: string | null;
    reference?: string | null;
    purchasedItems: string;
  }>;
  subscriptions: BillingSubscriptionSummary[];
  wallet: {
    balance: number;
    currency: string;
  };
  warnings: string[];
}

export const getBillingConfig = async (): Promise<BillingConfig> => {
  const { data } = await api.get<BillingConfig>('/billing/config');
  return data;
};

export const getBillingAccountSummary = async (): Promise<BillingAccountSummary> => {
  const { data } = await api.get<BillingAccountSummary>('/billing/account-summary');
  return data;
};

export const initiatePayment = async (payload: InitiatePaymentPayload): Promise<PaymentResponse> => {
  const { data } = await api.post<PaymentResponse>('/billing/initiate-payment', payload);
  return data;
};

export const previewPaymentIntent = async (payload: InitiatePaymentPayload): Promise<NextInvoice> => {
  const { data } = await api.post<NextInvoice>('/billing/payment-intent-preview', payload);
  return data;
};

export const updateTrialDeliveryOptions = async (payload: {
  channelId?: number | string;
  directPass: boolean;
  socialStream: boolean;
  hlsStandard: boolean;
}): Promise<{
  ok: boolean;
  channel: {
    id: number;
    hid?: string;
    name: string;
    outputLimit: number;
    socialStreamLimit: number;
    hlsLimit: number;
  };
}> => {
  const { data } = await api.post('/billing/trial-delivery', payload);
  return data;
};

export interface BillingAnchorQuote {
  eligible: boolean;
  blockers: string[];
  pending?: boolean;
  pendingOverrideId?: number | null;
  currentBillingCycleEndsAt: string;
  requestedBillingCycleEndsAt: string;
  extensionDays: number;
  fullPeriodDays: number | null;
  multiplier: number | null;
  renewalUsd: number | null;
  amountUsd: number;
  currency: string;
  amount: number;
  renewalAmount: number;
  renewalCurrency: string;
  payNowAvailable: boolean;
  deferAvailable: boolean;
  lockWindowDays: number;
  maxForwardShiftDays: number;
}

export const previewUserBillingAnchorChange = async (payload: {
  billingCycleEndsAt: string;
  paymentGateway?: 'manual';
}): Promise<BillingAnchorQuote> => {
  const { data } = await api.post<BillingAnchorQuote>('/billing/billing-anchor/preview', payload);
  return data;
};

export const deferBillingAnchorChange = async (payload: {
  billingCycleEndsAt: string;
}): Promise<{ ok: boolean; message: string; quote: BillingAnchorQuote }> => {
  const { data } = await api.post('/billing/billing-anchor/defer', payload);
  return data;
};

export const revertBillingAnchorChange = async (): Promise<{ ok: boolean; message: string; billingCycleEndsAt: string }> => {
  const { data } = await api.post('/billing/billing-anchor/revert');
  return data;
};

export const getNextInvoice = async (): Promise<NextInvoice> => {
  const { data } = await api.get<NextInvoice>('/billing/next-invoice');
  return data;
};

export const downloadInvoicePdf = async (invoiceId: number): Promise<Blob> => {
  const { data } = await api.get(`/billing/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
  return data;
};

export const sendInvoicePdf = async (invoiceId: number): Promise<{ ok: boolean; message: string }> => {
  const { data } = await api.post(`/billing/invoices/${invoiceId}/send-pdf`);
  return data;
};

export const retryInvoicePayment = async (
  invoiceId: number,
  payload: { paymentGateway: 'manual' }
): Promise<PaymentResponse> => {
  const { data } = await api.post<PaymentResponse>(`/billing/invoices/${invoiceId}/retry`, payload);
  return data;
};

export const cancelInvoicePaymentAttempt = async (
  invoiceId: number,
  reason: string
): Promise<{ ok: boolean; message: string }> => {
  const { data } = await api.post(`/billing/invoices/${invoiceId}/cancel-attempt`, { reason });
  return data;
};

export const downloadPaymentPdf = async (paymentId: number): Promise<Blob> => {
  const { data } = await api.get(`/billing/payments/${paymentId}/pdf`, { responseType: 'blob' });
  return data;
};

export const downloadPaymentHistoryPdf = async (): Promise<Blob> => {
  const { data } = await api.get('/billing/payments/pdf', { responseType: 'blob' });
  return data;
};

export type ChannelReportType = 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'MONTH_TO_DATE';

export interface ChannelReportRun {
  id: string;
  type: ChannelReportType;
  timezone: string;
  periodStart: string;
  periodEnd: string;
  status: 'GENERATING' | 'READY' | 'FAILED';
  generatedAt?: string | null;
  emailedAt?: string | null;
  fileName: string;
  summary?: {
    uptimePct?: number;
    dataCoveragePct?: number;
    overallStatus?: string;
  };
}

export const getChannelReports = async (type?: ChannelReportType): Promise<ChannelReportRun[]> => {
  const { data } = await api.get<{ items: ChannelReportRun[] }>('/billing/channel-reports', {
    params: { type, limit: 50 },
  });
  return Array.isArray(data?.items) ? data.items : [];
};

export const generateChannelReport = async (type: ChannelReportType): Promise<ChannelReportRun> => {
  const { data } = await api.post<ChannelReportRun>('/billing/channel-reports', { type });
  return data;
};

export const downloadChannelReportPdf = async (reportId: string): Promise<Blob> => {
  const { data } = await api.get(`/billing/channel-reports/${encodeURIComponent(reportId)}/pdf`, { responseType: 'blob' });
  return data;
};

export const getChannelReportPreference = async (): Promise<{ monthlyEmailEnabled: boolean }> => {
  const { data } = await api.get('/billing/channel-reports/preferences');
  return data;
};

export const updateChannelReportPreference = async (monthlyEmailEnabled: boolean): Promise<{ monthlyEmailEnabled: boolean }> => {
  const { data } = await api.put('/billing/channel-reports/preferences', { monthlyEmailEnabled });
  return data;
};

export const getResourceCancellations = async (): Promise<ResourceCancellation[]> => {
  const { data } = await api.get<{ items: ResourceCancellation[] }>('/billing/resource-cancellations');
  return Array.isArray(data?.items) ? data.items : [];
};

export const scheduleResourceCancellation = async (payload: {
  channelId: number | string;
  resourceType: string;
  resourceId?: string | null;
  quantity?: number;
  reason: string;
}): Promise<ResourceCancellation> => {
  const { data } = await api.post<{ item: ResourceCancellation }>('/billing/resource-cancellations', payload);
  return data.item;
};

export const undoResourceCancellation = async (id: number): Promise<ResourceCancellation> => {
  const { data } = await api.post<{ item: ResourceCancellation }>(`/billing/resource-cancellations/${id}/undo`);
  return data.item;
};

export const scheduleOutputPackageChange = async (payload: { channelId: number | string; packageCode: string; reason?: string }) => {
  const { data } = await api.post('/billing/output-package-change', payload);
  return data;
};

export const deleteResourceNow = async (payload: {
  channelId: number | string;
  resourceType: string;
  resourceId?: string | null;
  quantity?: number;
  reason: string;
  confirmation: 'DELETE NOW';
}): Promise<{ item: ResourceCancellation; message?: string }> => {
  const { data } = await api.post<{ item: ResourceCancellation; message?: string }>('/billing/resource-cancellations/delete-now', payload);
  return data;
};
